import React from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  PersonAdd,
  Security,
  Storage,
  BugReport,
  SettingsBackupRestore,
  AccountCircle,
  Dashboard
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';

const DebugTools: React.FC = () => {
  const { currentUser } = useAuth();
  
  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 8 }}>
        <Typography variant="h4" gutterBottom>Superior Payroll Debug Tools</Typography>
        <Typography color="text.secondary" paragraph>
          These tools help diagnose and fix common issues with the application.
        </Typography>
        
        <Paper sx={{ p: 3, my: 3, bgcolor: 'error.light' }}>
          <Typography variant="h6" fontWeight="bold" color="error">Critical Repair Tools</Typography>
          <Typography paragraph>
            Use these tools when you're experiencing login or profile loading issues:
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: '100%',
                  bgcolor: '#fff'
                }}
              >
                <Typography variant="h6" gutterBottom>Profile Repair</Typography>
                <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                  Fix issues with your user profile when it fails to load. Creates or repairs your profile directly.
                </Typography>
                <Button 
                  variant="contained" 
                  color="warning"
                  fullWidth
                  startIcon={<PersonAdd />}
                  onClick={() => window.location.href = '/fix-profile'}
                >
                  Fix My Profile
                </Button>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: '100%',
                  bgcolor: '#fff'
                }}
              >
                <Typography variant="h6" gutterBottom>Firebase Connection Test</Typography>
                <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                  Test connection to Firebase and diagnose database access issues.
                </Typography>
                <Button 
                  variant="contained" 
                  color="info"
                  fullWidth
                  startIcon={<Storage />}
                  onClick={() => window.location.href = '/firebase-connection-test'}
                >
                  Test Firebase Connection
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2">
                    Storage Tester
                  </Typography>
                  <Typography>
                    Test Firebase Storage connectivity and uploads directly. Helps diagnose issues with file uploads including profile photos.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" color="primary" component={RouterLink} to="/storage-test">
                    Open
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, my: 3 }}>
          <Typography variant="h6" gutterBottom>All Debug Tools</Typography>
          
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => window.location.href = '/fix-profile'}>
                <ListItemIcon><PersonAdd /></ListItemIcon>
                <ListItemText 
                  primary="Profile Creation Tool" 
                  secondary="Directly create or update your user profile" 
                />
              </ListItemButton>
            </ListItem>
            
            <Divider component="li" />
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => window.location.href = '/firebase-connection-test'}>
                <ListItemIcon><Storage /></ListItemIcon>
                <ListItemText 
                  primary="Firebase Connection Test" 
                  secondary="Test your connection to Firebase and diagnose database issues" 
                />
              </ListItemButton>
            </ListItem>
            
            <Divider component="li" />
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => window.location.href = '/auth-debug'}>
                <ListItemIcon><Security /></ListItemIcon>
                <ListItemText 
                  primary="Auth Debugger" 
                  secondary="Get detailed information about your authentication state" 
                />
              </ListItemButton>
            </ListItem>
            
            <Divider component="li" />
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => window.location.href = '/force-profile-creation'}>
                <ListItemIcon><SettingsBackupRestore /></ListItemIcon>
                <ListItemText 
                  primary="Force Profile Creation" 
                  secondary="Legacy tool for forcing profile creation" 
                />
              </ListItemButton>
            </ListItem>
            
            <Divider component="li" />
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => window.location.href = '/profile-troubleshooting'}>
                <ListItemIcon><BugReport /></ListItemIcon>
                <ListItemText 
                  primary="Profile Troubleshooting" 
                  secondary="Advanced troubleshooting for profile issues" 
                />
              </ListItemButton>
            </ListItem>
            
            <Divider component="li" />
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => window.location.href = '/debug/reset-friends'}>
                <ListItemIcon><PersonAdd /></ListItemIcon>
                <ListItemText 
                  primary="Reset Friend Requests" 
                  secondary="Reset all friend requests for testing purposes" 
                />
              </ListItemButton>
            </ListItem>
            
            <Divider component="li" />
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => window.location.href = '/debug/email-tester'}>
                <ListItemIcon><SettingsBackupRestore /></ListItemIcon>
                <ListItemText 
                  primary="Email Tester" 
                  secondary="Test email notifications and view email logs" 
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button 
            variant="outlined"
            startIcon={<Dashboard />}
            onClick={() => window.location.href = '/'}
          >
            Return to Home
          </Button>
          
          {currentUser && (
            <Button 
              variant="outlined"
              startIcon={<AccountCircle />}
              onClick={() => window.location.href = '/profile'}
            >
              Go to Profile
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default DebugTools; 